import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Trash2, Flame } from "lucide-react";
import { MACROS } from "@/lib/api";

const Ring = ({ value, goal, color }) => {
  const pct = Math.min(100, goal ? (value / goal) * 100 : 0);
  const data = [{ v: pct }, { v: 100 - pct }];
  return (
    <div className="relative h-[76px] w-[76px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="v"
            innerRadius={28}
            outerRadius={37}
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive={false}
          >
            <Cell fill={color} />
            <Cell fill="#EAE5D9" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <span className="absolute inset-0 grid place-items-center font-heading text-xs font-semibold text-deep-olive">
        {Math.round(pct)}%
      </span>
    </div>
  );
};

export const DailySummary = ({ stats }) => {
  if (!stats) return null;
  const { totals, goals, served, waste_calories, waste_pct, meal_count } = stats;
  const calPct = Math.min(100, goals.calories ? (totals.calories / goals.calories) * 100 : 0);

  return (
    <div className="grid gap-6 sm:grid-cols-2" data-testid="daily-summary">
      <div className="bg-surface rounded-xl border border-[#E8E3D9] p-6 hover:shadow-md transition-shadow duration-200">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">Today intake</p>
        <div className="mt-5 flex items-end gap-2">
          <span
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-deep-olive"
            data-testid="today-calories"
          >
            {Math.round(totals.calories)}
          </span>
          <span className="pb-1.5 text-sm text-stone-grey">/ {goals.calories} kcal</span>
        </div>
        <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-[#EAE5D9]">
          <div
            className="h-full rounded-full bg-forest transition-[width] duration-500"
            style={{ width: `${calPct}%` }}
          />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-[#E8E3D9] pt-5 text-sm">
          <div>
            <p className="text-stone-grey">Meals logged</p>
            <p className="font-heading text-lg font-medium text-deep-olive" data-testid="today-meal-count">
              {meal_count}
            </p>
          </div>
          <div>
            <p className="text-stone-grey flex items-center gap-1.5">
              <Flame className="h-3.5 w-3.5 text-ochre" /> Served
            </p>
            <p className="font-heading text-lg font-medium text-deep-olive">
              {Math.round(served.calories)} kcal
            </p>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2 rounded-lg bg-[#EAE5D9] px-4 py-3">
          <Trash2 className="h-4 w-4 text-terracotta shrink-0" />
          <p className="text-sm text-deep-olive" data-testid="today-waste">
            <span className="font-medium">{Math.round(waste_calories)} kcal</span> left on the
            plate ({waste_pct}% waste)
          </p>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-[#E8E3D9] p-6 hover:shadow-md transition-shadow duration-200">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">Macros today</p>
        <div className="mt-4 space-y-3" data-testid="macro-rings">
          {MACROS.map((m) => (
            <div key={m.key} className="flex items-center gap-4">
              <Ring value={totals[m.key]} goal={goals[m.key]} color={m.color} />
              <div>
                <p className="font-heading text-sm font-medium text-deep-olive">{m.label}</p>
                <p className="text-sm text-stone-grey" data-testid={`macro-${m.key}`}>
                  {Math.round(totals[m.key])}g of {goals[m.key]}g
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
